// ============================================================
// EventTypesCard.js — per-Client Event Type overlay.
// Backend route is per-eventTypeId; Save loops over dirty rows.
// ============================================================

import React, { useCallback, useEffect, useState } from 'react';
import Switch from '../../components/Switch';
import { toast } from '../../components/Toaster';
import {
  getClientEventTypeConfig, patchClientEventTypeConfigOne,
} from '../../api/adminClientConfig';
import { translateError } from '../../utils/errorMessages';
import StatusMessage from '../../components/StatusMessage';

export default function EventTypesCard({ clientId }) {
  const [items, setItems]   = useState([]);
  const [draft, setDraft]   = useState({}); // { eventTypeId: { isEnabled, titleOverride } }
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await getClientEventTypeConfig(clientId);
      const list = res.items || [];
      setItems(list);
      setDraft(Object.fromEntries(list.map((r) => [r.eventTypeId, {
        isEnabled: r.isEnabled, titleOverride: r.titleOverride || '',
      }])));
    } catch (err) { setError(translateError(err)); }
    finally { setLoading(false); }
  }, [clientId]);
  useEffect(() => { fetchData(); }, [fetchData]);

  function setRowField(id, key, value) {
    setDraft((prev) => ({ ...prev, [id]: { ...prev[id], [key]: value } }));
  }

  async function save() {
    setSaving(true);
    const dirty = items.filter((r) => {
      const d = draft[r.eventTypeId];
      return d.isEnabled !== r.isEnabled || (d.titleOverride || '') !== (r.titleOverride || '');
    });
    let succeeded = 0, failed = 0;
    for (const r of dirty) {
      const d = draft[r.eventTypeId];
      try {
        await patchClientEventTypeConfigOne(clientId, r.eventTypeId, {
          isEnabled: d.isEnabled,
          titleOverride: d.titleOverride ? d.titleOverride : null,
        });
        succeeded++;
      } catch (err) {
        failed++;
        toast.error(`${r.code}: ${translateError(err)}`);
      }
    }
    if (succeeded > 0) toast.success(`Saved ${succeeded} change${succeeded === 1 ? '' : 's'}`);
    if (failed === 0) await fetchData();
    setSaving(false);
  }

  if (loading) return <StatusMessage as="p" variant="loading" className="pc-state">Loading…</StatusMessage>;
  if (error)   return <StatusMessage as="p" variant="error" className="pc-state pc-state--error">{error}</StatusMessage>;

  return (
    <div>
      <table className="pc-table">
        <thead>
          <tr>
            <th>Event Type</th>
            <th>Title Override (blank = default)</th>
            <th style={{ width: 80 }}>Enabled</th>
          </tr>
        </thead>
        <tbody>
          {items.map((r) => {
            const d = draft[r.eventTypeId] || { isEnabled: r.isEnabled, titleOverride: r.titleOverride || '' };
            return (
              <tr key={r.eventTypeId}>
                <td>
                  <strong>{r.title}</strong>
                  <div style={{ fontSize: 11, color: '#888' }}>{r.code}</div>
                </td>
                <td>
                  <input
                    type="text"
                    value={d.titleOverride}
                    placeholder={r.title}
                    maxLength={200}
                    onChange={(e) => setRowField(r.eventTypeId, 'titleOverride', e.target.value)}
                  />
                </td>
                <td>
                  <Switch
                    checked={d.isEnabled}
                    onChange={(next) => setRowField(r.eventTypeId, 'isEnabled', next)}
                    ariaLabel={`${r.code} enabled`}
                  />
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
      <div className="pc-actions">
        <button type="button" className="pc-btn-primary" onClick={save} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
}
